import { useState, useEffect } from "react";
import axios from "axios";
import { Upload, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { CTABand } from "@/components/shared/CTABand";
import { COMPANY } from "@/data/company";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { title: "Incoming Material Checks", text: "Raw materials are verified at the source before entering production — quality begins before the first process step." },
  { title: "Process Control", text: "Written standards and procedures govern how we operate and control every stage of manufacture." },
  { title: "In-Process Inspection", text: "Rolls are inspected during manufacture, not just after it — deviations are caught where they occur." },
  { title: "Final Inspection", text: "Thorough, accurate and reliable final inspection confirms every roll against its specification." },
  { title: "Measurement & Testing", text: "Testing and measuring equipment verifies hardness, geometry and finish on every product." },
  { title: "Calibration Programme", text: "Inspection equipment is held to the highest accuracy through a maintained calibration programme." },
  { title: "Traceable Procedures", text: "Documented, repeatable procedures keep results consistent from one roll to the next." },
  { title: "Workplace Safety", text: "A standing safety committee keeps working conditions safe — so people can focus on quality." },
  { title: "Skilled Fabrication & Welding", text: "Certified welders, carefully trained on today's diverse alloys, handle all fabrication work." },
];

const CertificateSection = () => {
  const [cert, setCert] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchCert = async () => {
    try {
      const { data } = await axios.get(`${API}/certificate`);
      setCert(data);
    } catch {
      setCert({ available: false });
    }
  };
  useEffect(() => { fetchCert(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", "Quality Certificate");
      await axios.post(`${API}/certificate`, fd);
      toast.success("Certificate uploaded — it is now visible on this page.");
      fetchCert();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Use a PDF or image under 10 MB.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <section className="bg-ink-2 border-y border-line py-16 md:py-24 bg-blueprint">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <SectionHeading chapter="02" label="Certification" title="ISO 9001:2015 Certified" className="mb-10" />
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <Reveal>
            <div className="border border-line bg-ink p-7 md:p-8" data-testid="certification-details">
              <div className="flex items-start gap-4">
                <BadgeCheck size={26} className="text-amber-brand flex-none mt-1" aria-hidden="true" />
                <div className="w-full">
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-3">Quality Management System</p>
                  <p className="font-display font-black text-3xl text-steel-light mb-2">{COMPANY.certification.standard}</p>
                  <p className="text-steel text-sm leading-relaxed mb-6">Scope: {COMPANY.certification.scope}</p>
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 border-t border-line pt-5 text-sm">
                    <div>
                      <dt className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-steel/60 mb-1">Certificate No.</dt>
                      <dd className="text-steel-light">{COMPANY.certification.certificateNo}</dd>
                    </div>
                    <div>
                      <dt className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-steel/60 mb-1">Certification Body</dt>
                      <dd className="text-steel-light">{COMPANY.certification.body}</dd>
                    </div>
                    <div>
                      <dt className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-steel/60 mb-1">Issue Date</dt>
                      <dd className="text-steel-light">{COMPANY.certification.issueDate}</dd>
                    </div>
                    <div>
                      <dt className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-steel/60 mb-1">Valid Until</dt>
                      <dd className="text-steel-light">{COMPANY.certification.expiryDate}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-line bg-ink p-7 md:p-8" data-testid="certificate-upload-section">
              {cert?.available ? (
                <div>
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-4">Certificate on file</p>
                  <img
                    src={`${API}/certificate/file`}
                    alt="ISO 9001:2015 certificate issued to Vikas Rolls Private Limited by Quality Control Certification"
                    loading="lazy"
                    className="w-full max-w-md mx-auto border border-line"
                    data-testid="certificate-image"
                  />
                </div>
              ) : (
                <div>
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-steel mb-3">Certificate upload (site admin)</p>
                  <p className="text-steel text-sm leading-relaxed mb-5">
                    Upload the current certificate (PDF or image, max 10 MB) to display it here for visitors.
                  </p>
                  <label className="inline-flex items-center gap-3 border border-line px-5 py-3 text-sm text-steel-light cursor-pointer hover:border-amber-brand hover:text-amber-brand">
                    <Upload size={16} aria-hidden="true" />
                    {uploading ? "Uploading…" : "Upload Certificate"}
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="sr-only" onChange={onUpload} data-testid="certificate-upload-input" disabled={uploading} />
                  </label>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Quality() {
  useSEO({
    title: "Quality & Inspection",
    description:
      "Vikas Rolls is ISO 9001:2015 certified. Quality through incoming material checks, process control, in-process and final inspection, calibrated measurement, traceable procedures and certified welding.",
  });

  return (
    <>
      <PageHero
        chapter="Q"
        label="Quality & Inspection"
        title="Quality Is a Procedure, Not a Promise"
        sub="Vikas Rolls Private Limited operates an ISO 9001:2015 certified quality management system. From raw material selection to final inspection, every stage is governed by written standards, measured with calibrated equipment and carried out by skilled, trained people."
        crumbs={[{ label: "Quality" }]}
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-start mb-16">
            <Reveal>
              <div className="corner-marks border border-line overflow-hidden lg:sticky lg:top-28">
                <img src="/images/QUALITY_TEST.jpg" alt="Hardness testing of a roll cover at Vikas Rolls" loading="lazy" className="img-industrial w-full object-cover" />
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-steel px-4 py-3 border-t border-line bg-ink-2">
                  Fig. 01 — Cover hardness verification
                </p>
              </div>
            </Reveal>
            <Stagger className="grid sm:grid-cols-2 gap-px bg-line border border-line">
              {PILLARS.map((p, i) => (
                <StaggerItem key={p.title} className="bg-ink">
                  <div className="p-6 md:p-7 h-full bg-ink-2 hover:bg-ink-3 transition-colors duration-300">
                    <span className="font-mono-tech text-[10px] text-amber-brand">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-display font-bold text-lg text-steel-light mt-2 mb-2">{p.title}</h2>
                    <p className="text-sm text-steel leading-relaxed">{p.text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <CertificateSection />
      <CTABand />
    </>
  );
}
